import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import type { Swiper } from 'swiper/types';

import { Button } from '@components/Button';
import { RecipeCarousel } from '@components/RecipeCarousel';
import { RecipeDifficultyIcon } from '@components/RecipeDifficultyIcon';
import { RecipeFilters } from '@components/RecipeFilters';

import { useMediaQuery } from '@hooks/useMediaQuery';
import {
	CmsRecipeFilterOptions,
	CmsRecipeListingHighlight,
	RecipesQueryFilters,
} from '@interfaces/common';
import { minutesToISODuration } from '@utils/date';
import { breakpoints } from '@utils/device';
import { intlRecipeDifficulty, intlRecipeDuration } from '@utils/intl';

import styles from './RecipeListingHighlightSection.module.css';

interface RecipeListingHighlightSectionProps extends CmsRecipeListingHighlight {
	filters: CmsRecipeFilterOptions;
}

export const RecipeListingHighlightSection: React.VFC<
	RecipeListingHighlightSectionProps
> = ({
	text,
	showRecipesLabel,
	featuredRecipes: { title, recipes },
	filters,
}) => {
	const intl = useIntl();
	const router = useRouter();
	const isBreakpointLarge = useMediaQuery(breakpoints.lg);
	const [activeRecipeIndex, setActiveRecipeIndex] = useState(0);
	const [isFiltered, setIsFiltered] = useState(false);
	const [activeFilterBy, setActiveFilterBy] = useState<
		Partial<RecipesQueryFilters>
	>({});
	const styleProperties = {
		'--carousel-width': isBreakpointLarge ? '36vw' : '100vw',
		'--carousel-height': 'calc(var(--carousel-width) * 0.7481)',
	} as React.CSSProperties;

	const onSlideChange = (swiper: Swiper) => {
		setActiveRecipeIndex(swiper.realIndex);
	};

	const onFilter = async (
		filtersBy: Partial<RecipesQueryFilters>,
		isEmpty: boolean
	) => {
		setIsFiltered(!isEmpty);
		setActiveFilterBy(filtersBy);
	};

	const handleOnShowRecipes = () => {
		if (!isFiltered) return;

		router.push({
			pathname: '/recipes/filtered',
			query: Object.fromEntries(
				Object.entries(activeFilterBy).map(([key, value]) => [
					key,
					value?.join(','),
				])
			),
		});
	};

	return (
		<section
			className={styles.wrapper}
			data-testid="RecipeListingHighlightSection"
		>
			<div className={classNames(styles['description-section'], 'stack-block')}>
				<p className="fluid-font-t4">{text}</p>

				<RecipeFilters
					align="left"
					className={styles.filters}
					filterOptions={filters}
					onFilter={onFilter}
				/>

				<Button disabled={!isFiltered} onClick={handleOnShowRecipes}>
					{showRecipesLabel}
				</Button>
			</div>

			<div className={classNames(styles['featured-section'], 'stack-block')}>
				<div>
					<h2 className={classNames(styles.featured, 'fluid-font-t6-bold')}>
						{title}
					</h2>

					<div className={styles['carousel-wrapper']} style={styleProperties}>
						<div className={styles.carousel}>
							<RecipeCarousel
								featuredRecipes={recipes.map(({ image }) => image)}
								onSlideChange={onSlideChange}
								paginationClickable
								showNavigation={false}
								style={styleProperties}
							/>
						</div>
					</div>
				</div>

				{recipes[activeRecipeIndex] ? (
					<div>
						<h3 className="fluid-font-t4-bold">
							{recipes[activeRecipeIndex].metadata.title}
						</h3>

						<p className={classNames('stack-inline', 'fluid-font-t5')}>
							<span className={classNames(styles.difficulty, 'stack-inline')}>
								<RecipeDifficultyIcon
									difficulty={recipes[activeRecipeIndex].difficulty}
								/>

								<span>
									{intlRecipeDifficulty(
										intl,
										recipes[activeRecipeIndex].difficulty
									)}
								</span>
							</span>

							<time
								dateTime={minutesToISODuration(
									recipes[activeRecipeIndex].totalTime
								)}
							>
								{intlRecipeDuration(intl, recipes[activeRecipeIndex].totalTime)}
							</time>
						</p>
					</div>
				) : null}
			</div>
		</section>
	);
};
